import React from 'react';
import { useSelector } from 'react-redux';
import { Checkbox, FormControlLabel } from '@material-ui/core';
import isString from 'lodash-es/isString';
import classNames from 'clsx';

import { selectQuotation } from 'app/store/data/quotation';
import {
  CompareCepikDataEnum,
  FormsEnum,
  LabelEnum,
  MessageTypeEnum,
  ConsentsEnum,
} from 'app/shared/enums';
import { useCheckboxControl } from 'app/shared/hooks/controls/useCheckboxControl';
import { ControlContainer } from 'app/components/ControlContainer';
import { Alert } from 'app/components/Alert';
import { SafeHtml } from 'app/components/SafeHtml';
import { getIsFormMicroCenterActive } from 'app/store/selectors/selectIsFormMicroCenterActive';
import { CheckboxControlProps } from './CheckboxControl.interfaces';
import { useStyles } from './CheckboxControl.styles';
import { useUser } from 'app/shared/hooks/useUser';

export default function CheckboxControl(props: CheckboxControlProps) {
  const classes = useStyles();
  const {
    className,
    defaultAlertStyle,
    input,
    meta,
    tooltip,
    width,
    withRequiredMark,
    formName,
  } = props;
  const { isOperator } = useUser();
  const isFormMicroCenterActive = getIsFormMicroCenterActive(isOperator);

  const {
    checked,
    error,
    expandable,
    expanded,
    getFormattedLabel,
    getLabel,
    handleChange,
    handleClick,
    handleBlur,
    toggleExpanding,
  } = useCheckboxControl(props);

  const quotation = useSelector(selectQuotation);

  const isRegulationSelectedAndCepikNo =
    !quotation.consents?.cepik?.consent &&
    quotation.consents?.regulations?.consent;

  const showCepikInfoBox =
    input.name === 'consent.cepik' &&
    (formName === FormsEnum.CalculationForm ||
      formName === FormsEnum.CalculationFirstStepForm) &&
    isRegulationSelectedAndCepikNo;

  const showMarketingInfoBox =
    !isOperator &&
    !quotation.consents?.marketing?.consent &&
    input.name === 'consent.marketing#2' &&
    formName === FormsEnum.CalculationForm;

  const getLabelComponent = (input: { name: string }) => {
    const expansionToggleTitle = expanded
      ? LabelEnum.Collapse
      : LabelEnum.Expand;

    return (
      <span
        className={classes.label}
        style={input.name.startsWith('travel') ? { whiteSpace: 'nowrap' } : {}}
      >
        {getLabelWithoutEscape()}
        {expandable && (
          <>
            {` `}
            <span
              onClick={toggleExpanding}
              className={classNames(classes.expand, 'focusVisibleOutline')}
              tabIndex={0}
              onKeyDown={(e) => {
                if (
                  e.target === e.currentTarget &&
                  (e.key === 'Enter' || e.key === ' ')
                ) {
                  toggleExpanding(e);
                }
              }}
            >
              {expansionToggleTitle}
            </span>
          </>
        )}
      </span>
    );
  };

  const getLabelWithoutEscape = () => {
    let currentLabel = (expanded || !expandable
      ? getLabel()
      : getFormattedLabel()
    ).replace(/\s+$/, '');

    if (withRequiredMark)
      currentLabel += `<span class=${classes.consentRequired}> *</span>`;

    return isString(currentLabel) ? (
      <SafeHtml html={currentLabel} className={classes.label} />
    ) : (
      currentLabel
    );
  };

  return (
    <>
      <ControlContainer
        inputId={input.name}
        className={className}
        tooltip={tooltip}
        meta={meta}
        width={width}
        defaultAlertStyle={defaultAlertStyle}
        classes={{
          alert: classes.alert,
        }}
      >
        <FormControlLabel
          checked={checked}
          tabIndex={0}
          label={getLabelComponent(input)}
          className={`${
            isFormMicroCenterActive ? classes.controlNoCepik : classes.control
          } focusVisibleOutline`}
          onKeyDown={(e) => {
            if (
              e.target === e.currentTarget &&
              (e.key === 'Enter' || e.key === ' ')
            ) {
              e.preventDefault();
              handleChange();
            }
          }}
          control={
            <Checkbox
              name={input.name}
              onClick={handleClick}
              color="primary"
              className={classNames(classes.checkbox, {
                [classes.checkboxAlert]: error,
                [classes.checkboxHighlighted]: showCepikInfoBox,
              })}
              onChange={handleChange}
              onBlur={handleBlur}
              inputProps={{
                tabIndex: -1,
              }}
            />
          }
        />
      </ControlContainer>
      {showCepikInfoBox && (
        <Alert
          text={
            <SafeHtml
              html={CompareCepikDataEnum.CepikInfoConsentTitle}
              className={classes.title}
            />
          }
          type={MessageTypeEnum.Warning}
          className={classNames(classes.infoBox, classes.borderBox)}
        />
      )}
      {showMarketingInfoBox && (
        <Alert
          text={<SafeHtml html={ConsentsEnum.MarketingConsentInfo} />}
          type={MessageTypeEnum.Warning}
          className={classNames(classes.infoBox, classes.marketingInfoBox)}
        />
      )}
    </>
  );
}
